// src/pages/Onboarding/slides/EmailSlide.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SlideShell from '../SlideShell';
import { sendWelcomeEmail } from '../../../utils/Onboarding/autoMail';
import { trackMetaLead } from '../../../utils/metaPixel';
import { trackTikTokContact } from '../../../utils/tikTokPixel';

export default function EmailSlide({
  title = 'Please share your email',
  subtitle = 'We need your contact information to facilitate the process with the approptiate clinics.',
  currentStep,
  totalSteps,
  onNext,
  onBack,
  onGoToPhone = () => {},
  onAutoSave = null,
  sessionId = null,
  userName = '',
  theme = 'dark',
  animationDirection = 'forward',
  isTransitioning = false,
}) {
  const isDark = theme === 'dark';
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const isValid = useMemo(() => {
    // Basic email regex (simple, not RFC-perfect, but good UX)
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return re.test(String(email || '').trim());
  }, [email]);

  const labelText = isDark ? 'text-white' : 'text-gray-900';
  const inputText = isDark ? 'text-white' : 'text-gray-900';
  const inputBg = isDark ? 'bg-transparent' : 'bg-white';
  const inputBorder = isDark
    ? 'border-gray-600 focus:ring-white'
    : 'border-gray-300 focus:ring-black';
  const placeholderText = isDark ? 'placeholder-gray-500' : 'placeholder-gray-400';
  const pageBg = isDark ? 'bg-mm_black' : 'bg-white';

  const handleContinue = async () => {
    setTouched(true);
    if (!isValid || isSubmitting) return;
    
    setIsSubmitting(true);
    const clean = String(email).trim();
    
    try {
      // Send welcome email
      const emailResult = await sendWelcomeEmail({
        email: clean,
        fullName: userName,
        sessionId,
        source: 'email_slide',
        slideNumber: 28.5,
      });
      
      // Build data to pass to onNext, including welcome email result
      const submitData = {
        primaryContactEmailAddress: clean,
        preferredFollowUpChannel: 'email',
        welcomeEmailSent: emailResult.success,
      };
      
      if (!emailResult.success) {
        submitData.welcomeEmailError = emailResult.error;
      }
      
      // Also save to onAutoSave if provided (for Firestore sync)
      if (onAutoSave) {
        onAutoSave({
          welcomeEmailAttempts: [{
            email: clean,
            source: 'email_slide',
            slideNumber: 28.5,
            timestamp: new Date().toISOString(),
            success: emailResult.success,
            error: emailResult.success ? undefined : emailResult.error,
          }],
        });
      }
      
      // Track Meta Pixel Lead event for email submission
      trackMetaLead({
        content_name: 'email_slide',
        content_category: 'onboarding_contact',
      });
      // Track TikTok Pixel Contact event for email submission
      trackTikTokContact('onboarding_email');
      
      onNext?.(submitData);
    } catch (error) {
      console.error('[EmailSlide] Error during submission:', error);
      // Still proceed even if there's an error
      onNext?.({
        primaryContactEmailAddress: clean,
        preferredFollowUpChannel: 'email',
        welcomeEmailSent: false,
        welcomeEmailError: error.message || 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen ${pageBg}`}>
      <SlideShell
        title={title}
        subtitle={subtitle}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={handleContinue}
        onBack={onBack}
        nextLabel={isSubmitting ? 'Sending...' : 'Continue'}
        disabled={!isValid || isSubmitting}
        theme={theme}
        animationDirection={animationDirection}
        isTransitioning={isTransitioning}
        freezeViewportVars
        // Custom footer to control disclaimer, secondary, then primary
        footer={(
          <div className="space-y-3">
            <p className={`text-center text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Up to <span className="font-semibold">4x faster</span> responses with phone number contacts
            </p>
            <button
              type="button"
              onClick={onGoToPhone}
              disabled={isSubmitting}
              className="w-full py-3 px-6 rounded-full md:rounded-lg border text-white border-white bg-[#1f1f1f] hover:bg-black transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Enter phone number instead
            </button>
            {/* Primary button follows via SlideShell handlePrimaryClick */}
            <button
              type="button"
              onClick={handleContinue}
              disabled={!isValid || isSubmitting}
              className={`w-full py-3 px-6 rounded-full md:rounded-lg transition inline-flex items-center justify-center text-sm font-medium ${
                isDark
                  ? 'bg-white text-mm_black hover:bg-gray-100 disabled:bg-gray-600 disabled:cursor-not-allowed'
                  : 'bg-mm_black text-white hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed'
              }`}
            >
              {isSubmitting ? 'Sending...' : 'Continue'}
            </button>
          </div>
        )}
      >
        <div className="w-full max-w-md mx-auto">
          <label className={`block mb-2 text-sm font-medium ${labelText}`}>
            Your email
          </label>
          <input
            ref={inputRef}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="Email address"
            className={[
              'px-4 py-2 rounded-xl border w-full outline-none',
              inputText,
              inputBg,
              inputBorder,
              placeholderText,
              'focus:ring-2 transition',
            ].join(' ')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleContinue();
              }
            }}
          />
          {!isValid && touched && (
            <div className="mt-2 text-sm text-red-500">Please enter a valid email address.</div>
          )}
        </div>
      </SlideShell>
    </div>
  );
}
